/**
 * `/sign-in/choose` — **Choose an account** (#3005).
 *
 * Opens after **Sign out of ‹email›** while other accounts stay signed in
 * on this browser, and when Clerk ends the current session while others
 * live (`afterMultiSessionSingleSignOutUrl`). Works for a signed-in
 * person too: `SignInGate` does not send anyone away from it.
 *
 * Page answers (web.md): the person wants one of the accounts signed in
 * here; they see those accounts, each with avatar, name and email; they
 * pick one, or add another.
 */
import { Link } from "react-router-dom";

import { AccountList } from "../../components/shell/AccountList";
import { useAuth } from "../../lib/AuthContext";
import type { SignedInAccount } from "../../lib/accountSwitch";
import { AuthFrame, quietLinkClass } from "./AuthFrame";

export function ChooseAccountPage() {
  const auth = useAuth();
  const accounts = auth.accounts ?? [];

  function pick(account: SignedInAccount) {
    if (account.current) {
      window.location.assign("/");
      return;
    }
    void auth.switchAccount?.(account.key);
  }

  if (accounts.length === 0) {
    return (
      <AuthFrame title="Choose an account" lead="No account is signed in on this browser.">
        <Link to="/sign-in" className={quietLinkClass}>
          Sign in
        </Link>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Choose an account" lead="These accounts are signed in on this browser.">
      <div className="flex flex-col gap-2" data-testid="choose-account-list">
        <AccountList accounts={accounts} variant="page" onPick={pick} />
      </div>
    </AuthFrame>
  );
}
